import { Component, computed, input, OnInit, signal, viewChild } from '@angular/core';
import { isInputWithMapping, ToolInput } from '../../models/toolchain.models';
import { FormGroup } from '@angular/forms';
import { isTable, ModificationSetting, TableEditMode } from '../../models/tool.models';
import { TableColumn, DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { filterOptionalData } from '../../table-helper';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-table-input',
  templateUrl: './table-input.component.html',
  styleUrls: ['./table-input.component.scss'],
  imports: [MatDivider, DynamicTableComponent],
})
export class TableInputComponent implements OnInit {
  readonly input = input.required<ToolInput>();

  readonly form = input.required<FormGroup>();

  isEditable = signal(true);
  isSelectable = signal(true);
  tableEditType = signal<TableEditMode>('table');
  columnDef = signal<TableColumn[]>([]);
  modificationSettings = signal<Partial<ModificationSetting>>({});

  tableData = signal<Record<string, unknown>[]>([]);

  /** Inline table editor in this input. */
  private readonly dynamicTable = viewChild(DynamicTableComponent);

  /** Whether this table input has an active inline edit. */
  readonly isEditing = computed(() => this.dynamicTable()?.isEditing() ?? false);

  ngOnInit() {
    let tableData: unknown[] = [];

    // Set is editable based on modifiable columns
    const input = this.input();
    if (isTable(input.param)) {
      this.columnDef.set(input.param.columnDef);
      this.setEditableSetting();
    }

    if (isInputWithMapping(input)) {
      this.isSelectable.set(input.isSelectable);
      tableData = Array.isArray(input.valueToMap) ? input.valueToMap : [input.valueToMap];
    } else if (input.param.value) {
      tableData = Array.isArray(input.param.value) ? input.param.value : [input.param.value];
    }

    // filter for optional data
    filterOptionalData(this.columnDef(), tableData as Record<string, unknown>[]);
    this.tableData.set(tableData as Record<string, unknown>[]);
    this.form().controls[this.input().name].setValue(tableData);
  }

  /**
   * Method to apply all edit settings.
   * @private
   */
  private setEditableSetting() {
    const input = this.input();
    if (!isTable(input.param)) {
      return;
    }

    this.isEditable.set(input.isModifiable);

    if (this.isEditable()) {
      // do not make editable when no column can be edited
      if (!input.modifications && input.param.columnDef.every(col => !col.isModifiable)) {
        this.isEditable.set(false);
      }

      // Apply modifications
      if (input.modifications) {
        this.modificationSettings.set(input.modifications);
      }

      // Adapt editType if provided
      if (input.param.editType) {
        this.tableEditType.set(input.param.editType);
      }
    }
  }

  // eslint-disable-next-line
  onSelectionChanged(selectedTableData: any[]) {
    this.form().controls[this.input().name].setValue(selectedTableData);
  }
}
